import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import { DashboardLayout, DashboardHome } from "./pages/Dashboard";
import EbookPage from "./pages/EbookPage";
import JournalsPage from "./pages/JournalsPage";
import MeditationsPage from "./pages/MeditationsPage";
import PodcastPage from "./pages/PodcastPage";
import ProfilePage from "./pages/ProfilePage";
import OtherResourcesPage from "./pages/OtherResourcesPage";
import AdminDashboard from "./pages/AdminDashboard";
import { FreeClassesPage, PremiumClassesPage, SessionReplaysPage } from "./pages/ClassesPage";
import { FreeCommunityPage, FoundingCommunityPage, VipCommunityPage } from "./pages/CommunityPage";
import { PricingPage, PaymentSuccessPage } from "./pages/PricingPage";
import RetreatPage from "./pages/RetreatPage";
import { VipClassesPage, ClassLibraryPage } from "./pages/VipPages";
import EventsCalendarPage from "./pages/EventsCalendarPage";
import AlignmentLabPage from "./pages/AlignmentLabPage";
import AlignmentLabLandingPage from "./pages/AlignmentLabLandingPage";
import CoachPocketPage from "./pages/CoachPocketPage";
import VipOtherResourcesPage from "./pages/VipOtherResourcesPage";
import MessagesPage from "./pages/MessagesPage";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Page title hook
const usePageTitle = (title) => {
  useEffect(() => {
    document.title = title ? `${title} | Illuminate Your Worth` : 'Illuminate Your Worth';
  }, [title]);
};

// Public page wrapper with title
const PageWithTitle = ({ title, children }) => {
  usePageTitle(title);
  return children;
};

// Dashboard Wrapper with Layout
const DashboardWrapper = ({ title, children }) => {
  usePageTitle(title);
  return (
    <ProtectedRoute>
      <DashboardLayout>
        {children}
      </DashboardLayout>
    </ProtectedRoute>
  );
};

// Seed data on first load (for demo purposes)
const SeedData = () => {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API_URL}/seed`);
        console.log("Seed data created");
      } catch (error) {
        console.log("Seed data already exists or failed:", error.message);
      }
    };
    seedData();
  }, []);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <SeedData />
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PageWithTitle title={null}><LandingPage /></PageWithTitle>} />
          <Route path="/login" element={<PageWithTitle title="Sign In"><LoginPage /></PageWithTitle>} />
          <Route path="/register" element={<PageWithTitle title="Join"><RegisterPage /></PageWithTitle>} />
          <Route path="/forgot-password" element={<PageWithTitle title="Forgot Password"><ForgotPasswordPage /></PageWithTitle>} />
          <Route path="/reset-password" element={<PageWithTitle title="Reset Password"><ResetPasswordPage /></PageWithTitle>} />
          <Route path="/pricing" element={<PageWithTitle title="Pricing"><PricingPage /></PageWithTitle>} />
          <Route path="/alignment-lab" element={<PageWithTitle title="30-Day Alignment Lab"><AlignmentLabLandingPage /></PageWithTitle>} />
          <Route path="/payment/success" element={<PageWithTitle title="Payment Success"><PaymentSuccessPage /></PageWithTitle>} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<DashboardWrapper title="Dashboard"><DashboardHome /></DashboardWrapper>} />
          <Route path="/dashboard/ebook" element={<DashboardWrapper title="Ebook"><EbookPage /></DashboardWrapper>} />
          <Route path="/dashboard/journals" element={<DashboardWrapper title="Journals"><JournalsPage /></DashboardWrapper>} />
          <Route path="/dashboard/meditations" element={<DashboardWrapper title="Meditations"><MeditationsPage /></DashboardWrapper>} />
          <Route path="/dashboard/podcast" element={<DashboardWrapper title="Podcast"><PodcastPage /></DashboardWrapper>} />
          <Route path="/dashboard/classes" element={<DashboardWrapper title="Classes"><FreeClassesPage /></DashboardWrapper>} />
          <Route path="/dashboard/community" element={<DashboardWrapper title="Community"><FreeCommunityPage /></DashboardWrapper>} />
          <Route path="/dashboard/resources" element={<DashboardWrapper title="Resources"><OtherResourcesPage /></DashboardWrapper>} />
          <Route path="/dashboard/retreat" element={<DashboardWrapper title="Retreat 2027"><RetreatPage /></DashboardWrapper>} />
          <Route path="/dashboard/profile" element={<DashboardWrapper title="My Profile"><ProfilePage /></DashboardWrapper>} />
          <Route path="/dashboard/messages" element={<DashboardWrapper title="Messages"><MessagesPage /></DashboardWrapper>} />
          <Route path="/dashboard/messages/:conversationId" element={<DashboardWrapper title="Messages"><MessagesPage /></DashboardWrapper>} />
          
          {/* Admin Routes */}
          <Route path="/dashboard/admin" element={<DashboardWrapper title="Admin Dashboard"><AdminDashboard /></DashboardWrapper>} />
          
          {/* Premium Routes */}
          <Route path="/dashboard/premium-classes" element={<DashboardWrapper title="Premium Classes"><PremiumClassesPage /></DashboardWrapper>} />
          <Route path="/dashboard/founding-community" element={<DashboardWrapper title="Founding Community"><FoundingCommunityPage /></DashboardWrapper>} />
          <Route path="/dashboard/events" element={<DashboardWrapper title="Events Calendar"><EventsCalendarPage /></DashboardWrapper>} />
          <Route path="/dashboard/session-replays" element={<DashboardWrapper title="Session Replays"><SessionReplaysPage /></DashboardWrapper>} />

          {/* VIP Routes */}
          <Route path="/dashboard/vip-classes" element={<DashboardWrapper title="VIP Classes"><VipClassesPage /></DashboardWrapper>} />
          <Route path="/dashboard/class-library" element={<DashboardWrapper title="Class Library"><ClassLibraryPage /></DashboardWrapper>} />
          <Route path="/dashboard/coach-pocket" element={<DashboardWrapper title="Coach in Your Pocket"><CoachPocketPage /></DashboardWrapper>} />
          <Route path="/dashboard/vip-community" element={<DashboardWrapper title="VIP Community"><VipCommunityPage /></DashboardWrapper>} />
          <Route path="/dashboard/vip-resources" element={<DashboardWrapper title="VIP Resources"><VipOtherResourcesPage /></DashboardWrapper>} />

          {/* Alignment Lab Routes */}
          <Route path="/dashboard/alignment-lab" element={<DashboardWrapper title="30-Day Alignment Lab"><AlignmentLabPage /></DashboardWrapper>} />
          <Route path="/alignment-lab/success" element={<DashboardWrapper title="Alignment Lab"><AlignmentLabPage /></DashboardWrapper>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
